import { AlertCircle, ChevronLeft, Loader } from "lucide-react";
import React, { useEffect, useState } from "react";
import { API_BASE } from "../../../../consts";
import authService from "../../../../services/AuthService";
import WalmartItemForm from "./WalmartItemForm";
import WalmartNewListingFlow from "./WalmartNewListingFlow";

const api = (path, opts) => authService.authenticatedFetch(`${API_BASE}${path}`, opts);
const PRODUCT_ID_TYPES = ["UPC", "GTIN", "ISBN", "EAN"];

export const COLOR_CATEGORIES = ["Beige", "Black", "Blue", "Bronze", "Brown", "Clear", "Gold", "Gray", "Green", "Multicolor", "Off-White", "Orange", "Pink", "Purple", "Red", "Silver", "White", "Yellow"];
const PLUSH_TYPES = ["Figural Pillows", "Plush Toys", "Stuffed Animals"];

const parseAgeString = (s) => {
    if (!s) return null;
    const m = String(s).match(/([\d.]+)\s*(year|month)/i);
    if (!m) return null;
    return { measure: parseFloat(m[1]), unit: m[2].toLowerCase().startsWith("year") ? "years" : "months" };
};

/* ─── SF: identifier lookup, then hands off to the shared WalmartItemForm ─── */
const MPItemFlow = ({ initialProduct, batchId, onAddToBatch, onBack, buylistFields }) => {
    const [stage, setStage] = useState("looking_up"); // 'looking_up' | 'need_identifier' | 'ready'
    const [lookupNote, setLookupNote] = useState("");
    const [idType, setIdType] = useState("UPC");
    const [idValue, setIdValue] = useState("");
    const [inventoryData, setInventoryData] = useState(null);
    const [existingSku, setExistingSku] = useState(null); // set if this UPC/GTIN already exists in our catalog
    const [existingPrice, setExistingPrice] = useState(null); // this seller's own current listed price for that existing item

    useEffect(() => {
        const knownUpc = initialProduct.upc || initialProduct.searched_upc;
        const knownGtin = initialProduct.gtin || initialProduct.searched_gtin;
        if (knownUpc) { setIdType("UPC"); setIdValue(knownUpc); fetchInventoryThenReady("upc", knownUpc); }
        else if (knownGtin) { setIdType("GTIN"); setIdValue(knownGtin); fetchInventoryThenReady("gtin", knownGtin); }
        else autoLookupIdentifier();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const checkExistingSku = async (idTypeForCheck, idValueForCheck) => {
        try {
            const resp = await api(`/api/walmart/api/check-existing-item?identifier_type=${idTypeForCheck}&identifier_value=${encodeURIComponent(idValueForCheck)}`);
            const data = await resp.json();
            if (resp.ok && data.exists && data.sku) {
                setExistingSku(data.sku);
                if (data.price != null) setExistingPrice(data.price);
            }
        } catch { /* non-fatal — just no suggested SKU */ }
    };

    // Only flip to "ready" (which mounts WalmartItemForm and triggers its
    // one-time prefill) AFTER the scrape actually resolves — mounting the
    // form before this data arrives means it prefills from an empty object
    // and the real scraped data arrives too late to ever be used.
    const fetchInventoryThenReady = async (idTypeForCheck, idValueForCheck) => {
        try {
            const resp = await api(`/api/walmart/inventory-basic/${encodeURIComponent(initialProduct.item_id)}`);
            const data = await resp.json();
            if (resp.ok && !data.error) setInventoryData(data);
        } catch { /* non-fatal — proceed with whatever we have (possibly nothing) */ }
        await checkExistingSku(idTypeForCheck, idValueForCheck);
        setStage("ready");
    };

    const autoLookupIdentifier = async () => {
        setStage("looking_up");
        try {
            const resp = await api(`/api/walmart/inventory-basic/${encodeURIComponent(initialProduct.item_id)}`);
            const data = await resp.json();
            if (!resp.ok || data.error) throw new Error(data.error || "Lookup failed");
            setInventoryData(data);
            if (data.upc) { setIdType("UPC"); setIdValue(data.upc); await checkExistingSku("upc", data.upc); setStage("ready"); }
            else if (data.gtin) { setIdType("GTIN"); setIdValue(data.gtin); await checkExistingSku("gtin", data.gtin); setStage("ready"); }
            else { setLookupNote("Found the product but no UPC/GTIN on the page."); setStage("need_identifier"); }
        } catch {
            setLookupNote("Automatic lookup failed for this item.");
            setStage("need_identifier");
        }
    };

    if (stage === "looking_up") {
        return (
            <div className="bg-white rounded-2xl shadow-lg p-10 flex flex-col items-center text-gray-500 text-sm">
                <Loader size={24} className="animate-spin mb-3 text-teal-500" /> Looking up product data automatically...
            </div>
        );
    }

    if (stage === "need_identifier") {
        return (
            <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 mb-4 text-xs text-orange-700 flex items-start gap-2">
                    <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                    {lookupNote} Enter one below to continue.
                </div>
                <div className="flex gap-2 mb-3 flex-wrap">
                    {PRODUCT_ID_TYPES.map((t) => (
                        <button key={t} onClick={() => setIdType(t)}
                            className={`px-4 py-2 text-sm font-bold rounded-lg border-2 transition ${idType === t ? "border-teal-500 bg-teal-50 text-teal-700" : "border-gray-200 text-gray-500"}`}>
                            {t}
                        </button>
                    ))}
                </div>
                <input value={idValue} onChange={(e) => setIdValue(e.target.value)} placeholder={`Enter ${idType}`}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm font-mono mb-4 focus:outline-none focus:border-teal-500" autoFocus />
                <button onClick={() => setStage("ready")} disabled={!idValue.trim()}
                    className="w-full py-3 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-xl font-bold hover:opacity-90 transition disabled:opacity-40">
                    Continue
                </button>
            </div>
        );
    }

    const inv = inventoryData || {};
    const minAge = parseAgeString(inv.min_recommended_age);
    const maxAge = parseAgeString(inv.max_recommended_age) || (inv.age_group ? { measure: 99, unit: "years" } : null);
    const heightMU = inv.dimensions?.height != null ? { measure: inv.dimensions.height, unit: "in" } : undefined;
    const widthMU = inv.dimensions?.width != null ? { measure: inv.dimensions.width, unit: "in" } : undefined;
    const depthMU = inv.dimensions?.length != null ? { measure: inv.dimensions.length, unit: "in" } : undefined;

    const keyFeaturesList = (inv.key_features || []).map((k) => k.description).filter(Boolean);
    const keyFeaturesJoined = keyFeaturesList.length > 0 ? keyFeaturesList.join(", ") : (inv.features || "");

    // Defensively mapped under every plausible schema key alias we've seen so
    // far, since Walmart's exact field names vary by product-type category
    // (e.g. "assembledProductLength" vs "assembledProductDepth"). Any key not
    // matched here still gets caught by "Fill Missing with AI".
    const knownValues = {
        asin: inv.asin || "",
        ingredients: inv.ingredients_statement || "", // schema key matches directly — no generic matching needed for this one
        productName: initialProduct.title || inv.name || "",
        brand: initialProduct.brand || inv.brand || "",
        brandName: initialProduct.brand || inv.brand || "",
        manufacturer: inv.manufacturer || initialProduct.brand || "",
        shortDescription: (inv.short_description || initialProduct.description || "").replace(/<[^>]+>/g, " ").trim(),
        siteDescription: (inv.short_description || inv.long_description || initialProduct.description || "").replace(/<[^>]+>/g, " ").trim(),
        mainImageUrl: initialProduct.image_url || inv.image || "",
        price: inv.price != null ? String(inv.price) : (initialProduct.price != null ? String(initialProduct.price) : ""),
        ShippingWeight: inv.weight != null ? String(inv.weight) : "",
        condition: "New",
        category: initialProduct.category || inv.category || null,
        sub_category: initialProduct.sub_category || inv.sub_category || null,

        color: inv.color || "",
        colorCategory: COLOR_CATEGORIES.includes(inv.color) ? inv.color : "",

        material: inv.material || "",
        fillMaterial: inv.fill_material || "",

        ageGroup: inv.age_group || "",

        keyFeatures: keyFeaturesJoined,
        features: keyFeaturesJoined,
        // Raw array (not comma-joined) — used to prefill the "Key Features"
        // repeatable-entry GROUP field (buildAutoGroupEntries in
        // WalmartItemForm), which is how this actually renders on the form.
        keyFeaturesList,

        pattern: inv.pattern || "",
        theme: inv.theme || "",
        occasion: inv.occasion || "",
        character: inv.character || "",
        count: inv.count != null ? String(inv.count) : "",
        sizeDescriptor: inv.size_descriptor || "",

        stuffed_animal_and_plush_toy_type: PLUSH_TYPES.includes(inv.animal_type) ? inv.animal_type : "",

        isProp65WarningRequired: "No",
        has_written_warranty: "No",
        smallPartsWarnings: "0 - No warning applicable",
        electronicsIndicator: "No",
        batteryTechnologyType: "Does Not Contain a Battery",
        chemicalAerosolPesticide: "No",
        shipsInOriginalPackaging: "No",
        MustShipAlone: "No",
        countPerPack: "1",
        multipackQuantity: "1",
        productNetContentMeasure: "1",
        productNetContentUnit: "Each",

        // Height only has one common name across categories so far
        assembledProductHeight: heightMU,
        // Width also seems consistent
        assembledProductWidth: widthMU,
        // Third dimension varies by category — set under every alias seen so far
        assembledProductLength: depthMU,
        assembledProductDepth: depthMU,
        // Weight is a PLAIN scalar in most schemas (unlike height/width/length,
        // which are measure+unit objects) — reuses the exact same weight value
        // already used for ShippingWeight, so the AI is never asked to invent
        // a separate number for what's really the same known measurement.
        assembledProductWeight: inv.weight != null ? String(inv.weight) : "",

        minimumRecommendedAge: minAge || undefined,
        maximumRecommendedAge: maxAge || undefined,
    };

    // Secondary images: everything Walmart's own page returned except
    // whichever one is being used as the main image — this is what actually
    // fills productSecondaryImageURL, which previously never received any
    // value at all in this flow.
    const secondaryFromListing = (inv.images || []).filter((u) => u !== initialProduct.image_url);

    return (
        <WalmartItemForm
            productType={initialProduct.product_type}
            knownValues={knownValues}
            identifierType={idType}
            identifierValue={idValue}
            imageUrl={initialProduct.image_url}
            secondaryImageUrls={secondaryFromListing}
            itemId={initialProduct.item_id}
            batchId={batchId}
            suggestedSku={existingSku}
            existingPrice={existingPrice}
            buylistFields={buylistFields}
            productImages={inv.images || []}
            specifications={inv.specifications || []}
            onAddToBatch={onAddToBatch}
            onBack={onBack}
        />
    );
};

const WFS_MP_ITEM_VERSION = "5.0.20260501-19_21_29-api";

/* ─── WFS: identifier lookup + existing-SF-inventory check, then hands off
   to the shared WalmartItemForm with feedType MP_WFS_ITEM (brand new item)
   or OMNI_WFS (converting an item that already exists in our catalog). ─── */
const MPWfsItemFlow = ({ initialProduct, batchId, onAddToBatch, onBack, buylistFields }) => {
    const [stage, setStage] = useState("looking_up"); // 'looking_up' | 'need_identifier' | 'confirm_zero_sf' | 'ready'
    const [lookupNote, setLookupNote] = useState("");
    const [idType, setIdType] = useState("UPC");
    const [idValue, setIdValue] = useState("");
    const [inventoryData, setInventoryData] = useState(null);
    const [existingItem, setExistingItem] = useState(null); // { catalog_item_id, sku, sf_stock } | null
    const [zeroing, setZeroing] = useState(false);
    const [zeroingError, setZeroingError] = useState("");

    useEffect(() => {
        const knownUpc = initialProduct.upc || initialProduct.searched_upc;
        const knownGtin = initialProduct.gtin || initialProduct.searched_gtin;
        if (knownUpc) { setIdType("UPC"); setIdValue(knownUpc); fetchInventoryThenCheckSf("upc", knownUpc); }
        else if (knownGtin) { setIdType("GTIN"); setIdValue(knownGtin); fetchInventoryThenCheckSf("gtin", knownGtin); }
        else autoLookupIdentifier();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const checkExistingSfInventory = async (idTypeForCheck, idValueForCheck) => {
        try {
            const resp = await api(`/api/walmart/api/check-existing-item?identifier_type=${idTypeForCheck}&identifier_value=${encodeURIComponent(idValueForCheck)}`);
            const data = await resp.json();
            if (resp.ok && data.exists) {
                setExistingItem(data);
                if (data.sf_stock && data.sf_stock > 0) {
                    setStage("confirm_zero_sf");
                    return;
                }
            } else {
                setExistingItem(null);
            }
        } catch {
            setExistingItem(null);
        }
        setStage("ready");
    };

    const fetchInventoryThenCheckSf = async (idTypeForCheck, idValueForCheck) => {
        try {
            const resp = await api(`/api/walmart/inventory-basic/${encodeURIComponent(initialProduct.item_id)}`);
            const data = await resp.json();
            if (resp.ok && !data.error) setInventoryData(data);
        } catch { /* non-fatal — proceed with whatever we have (possibly nothing) */ }
        await checkExistingSfInventory(idTypeForCheck, idValueForCheck);
    };

    const autoLookupIdentifier = async () => {
        setStage("looking_up");
        try {
            const resp = await api(`/api/walmart/inventory-basic/${encodeURIComponent(initialProduct.item_id)}`);
            const data = await resp.json();
            if (!resp.ok || data.error) throw new Error(data.error || "Lookup failed");
            setInventoryData(data);
            if (data.upc) { setIdType("UPC"); setIdValue(data.upc); return checkExistingSfInventory("upc", data.upc); }
            if (data.gtin) { setIdType("GTIN"); setIdValue(data.gtin); return checkExistingSfInventory("gtin", data.gtin); }
            setLookupNote("Found the product but no UPC/GTIN on the page.");
            setStage("need_identifier");
        } catch {
            setLookupNote("Automatic lookup failed for this item.");
            setStage("need_identifier");
        }
    };

    const handleZeroSf = async (shouldZero) => {
        if (shouldZero && existingItem) {
            setZeroing(true);
            setZeroingError("");
            try {
                const resp = await api("/api/walmart/api/inventory/sf", {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ catalog_item_id: existingItem.catalog_item_id, sku: existingItem.sku, quantity: 0 }),
                });
                const data = await resp.json();
                if (!resp.ok) throw new Error(data.error || "Failed to zero SF inventory");
            } catch (err) {
                setZeroingError(err.message || "Failed to zero SF inventory — you can zero it manually from Catalog.");
            } finally {
                setZeroing(false);
            }
        }
        setStage("ready");
    };

    if (stage === "looking_up") {
        return (
            <div className="bg-white rounded-2xl shadow-lg p-10 flex flex-col items-center text-gray-500 text-sm">
                <Loader size={24} className="animate-spin mb-3 text-teal-500" /> Looking up product data automatically...
            </div>
        );
    }

    if (stage === "confirm_zero_sf") {
        return (
            <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-4">
                    <p className="font-bold text-gray-800 mb-1 flex items-center gap-2">
                        <AlertCircle size={16} className="text-orange-500" /> You have SF inventory for this item
                    </p>
                    <p className="text-sm text-gray-600">
                        This item currently has <span className="font-semibold">{existingItem.sf_stock}</span> units in Seller-Fulfilled
                        inventory (SKU: {existingItem.sku}). Would you like to zero it out now that you're sending it to WFS?
                    </p>
                </div>
                {zeroingError && <div className="mb-3 bg-red-50 border border-red-200 rounded-xl p-3 text-red-700 text-xs">{zeroingError}</div>}
                <div className="flex gap-3">
                    <button onClick={() => handleZeroSf(false)} disabled={zeroing}
                        className="flex-1 py-3 border-2 border-gray-200 text-gray-600 rounded-xl font-bold hover:bg-gray-50 transition disabled:opacity-50">
                        No, Skip
                    </button>
                    <button onClick={() => handleZeroSf(true)} disabled={zeroing}
                        className="flex-1 py-3 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-xl font-bold hover:opacity-90 transition disabled:opacity-50">
                        {zeroing ? "Zeroing..." : "Yes, Zero SF Inventory"}
                    </button>
                </div>
            </div>
        );
    }

    if (stage === "need_identifier") {
        return (
            <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 mb-4 text-xs text-orange-700 flex items-start gap-2">
                    <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                    {lookupNote} Enter one below to continue.
                </div>
                <div className="flex gap-2 mb-3 flex-wrap">
                    {PRODUCT_ID_TYPES.map((t) => (
                        <button key={t} onClick={() => setIdType(t)}
                            className={`px-4 py-2 text-sm font-bold rounded-lg border-2 transition ${idType === t ? "border-teal-500 bg-teal-50 text-teal-700" : "border-gray-200 text-gray-500"}`}>
                            {t}
                        </button>
                    ))}
                </div>
                <input value={idValue} onChange={(e) => setIdValue(e.target.value)} placeholder={`Enter ${idType}`}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm font-mono mb-4 focus:outline-none focus:border-teal-500" autoFocus />
                <button onClick={() => checkExistingSfInventory(idType.toLowerCase(), idValue)} disabled={!idValue.trim()}
                    className="w-full py-3 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-xl font-bold hover:opacity-90 transition disabled:opacity-40">
                    Continue
                </button>
            </div>
        );
    }

    // stage === "ready"
    // Always MP_WFS_ITEM — OMNI_WFS isn't working reliably right now, and
    // MP_WFS_ITEM is documented as create-or-update, so resubmitting it for
    // an item that already exists should update/convert it in place too.
    const feedType = "MP_WFS_ITEM";

    const inv = inventoryData || {};
    const minAge = parseAgeString(inv.min_recommended_age);
    const maxAge = parseAgeString(inv.max_recommended_age) || (inv.age_group ? { measure: 99, unit: "years" } : null);
    const heightMU = inv.dimensions?.height != null ? { measure: inv.dimensions.height, unit: "in" } : undefined;
    const widthMU = inv.dimensions?.width != null ? { measure: inv.dimensions.width, unit: "in" } : undefined;
    const depthMU = inv.dimensions?.length != null ? { measure: inv.dimensions.length, unit: "in" } : undefined;

    const keyFeaturesList = (inv.key_features || []).map((k) => k.description).filter(Boolean);
    const keyFeaturesJoined = keyFeaturesList.length > 0 ? keyFeaturesList.join(", ") : (inv.features || "");

    const knownValues = {
        asin: inv.asin || "",
        ingredients: inv.ingredients_statement || "", // schema key matches directly — no generic matching needed for this one
        productName: initialProduct.title || inv.name || "",
        brand: initialProduct.brand || inv.brand || "",
        brandName: initialProduct.brand || inv.brand || "",
        manufacturer: inv.manufacturer || initialProduct.brand || "",
        shortDescription: (inv.short_description || initialProduct.description || "").replace(/<[^>]+>/g, " ").trim(),
        siteDescription: (inv.short_description || inv.long_description || initialProduct.description || "").replace(/<[^>]+>/g, " ").trim(),
        mainImageUrl: initialProduct.image_url || inv.image || "",
        price: inv.price != null ? String(inv.price) : (initialProduct.price != null ? String(initialProduct.price) : ""),
        ShippingWeight: inv.weight != null ? String(inv.weight) : "",
        // WFS's TradeItem.each container uses these bare key names instead of
        // MP_ITEM's measure/unit assembledProduct* fields — same underlying
        // data, aliased so knownValues?.[f.key] actually finds it for WFS.
        eachDepth: inv.dimensions?.length != null ? String(inv.dimensions.length) : "",
        eachHeight: inv.dimensions?.height != null ? String(inv.dimensions.height) : "",
        eachWidth: inv.dimensions?.width != null ? String(inv.dimensions.width) : "",
        eachWeight: inv.weight != null ? String(inv.weight) : "",
        condition: "New",
        category: initialProduct.category || inv.category || null,
        sub_category: initialProduct.sub_category || inv.sub_category || null,

        color: inv.color || "",
        colorCategory: COLOR_CATEGORIES.includes(inv.color) ? inv.color : "",

        material: inv.material || "",
        fillMaterial: inv.fill_material || "",

        ageGroup: inv.age_group || "",

        keyFeatures: keyFeaturesJoined,
        features: keyFeaturesJoined,
        keyFeaturesList,

        pattern: inv.pattern || "",
        theme: inv.theme || "",
        occasion: inv.occasion || "",
        character: inv.character || "",
        count: inv.count != null ? String(inv.count) : "",
        sizeDescriptor: inv.size_descriptor || "",

        stuffed_animal_and_plush_toy_type: PLUSH_TYPES.includes(inv.animal_type) ? inv.animal_type : "",

        isProp65WarningRequired: "No",
        has_written_warranty: "No",
        smallPartsWarnings: "0 - No warning applicable",
        electronicsIndicator: "No",
        batteryTechnologyType: "Does Not Contain a Battery",
        chemicalAerosolPesticide: "No",
        shipsInOriginalPackaging: "No",
        MustShipAlone: "No",
        countPerPack: "1",
        multipackQuantity: "1",
        productNetContentMeasure: "1",
        productNetContentUnit: "Each",

        assembledProductHeight: heightMU,
        assembledProductWidth: widthMU,
        assembledProductLength: depthMU,
        assembledProductDepth: depthMU,
        assembledProductWeight: inv.weight != null ? String(inv.weight) : "",

        minimumRecommendedAge: minAge || undefined,
        maximumRecommendedAge: maxAge || undefined,
    };

    const secondaryFromListing = (inv.images || []).filter((u) => u !== initialProduct.image_url);

    return (
        <WalmartItemForm
            productType={initialProduct.product_type}
            knownValues={knownValues}
            identifierType={idType}
            identifierValue={idValue}
            imageUrl={initialProduct.image_url}
            secondaryImageUrls={secondaryFromListing}
            itemId={initialProduct.item_id}
            batchId={batchId}
            feedType={feedType}
            version={WFS_MP_ITEM_VERSION}
            suggestedSku={existingItem?.sku}
            existingPrice={existingItem?.price}
            buylistFields={buylistFields}
            productImages={inv.images || []}
            specifications={inv.specifications || []}
            onAddToBatch={onAddToBatch}
            onBack={onBack}
        />
    );
};

/* ─── Main component — routes by batch type + new vs existing ─── */
const WalmartItemDetails = ({ batchType, batchId, initialProduct, retryItem, onAddToBatch, onBack, prefillUpc, prefillName, prefillDescription, prefillImageUrl, buylistFields }) => {
    const isNew = !initialProduct && !retryItem;

    if (retryItem) {
        const productType = Object.keys(retryItem.payload?.Visible || {})[0] || null;
        // Must fetch the SAME schema the item was originally built from, or
        // hydrateFromInitialItem looks up stored values at the wrong paths
        // entirely (e.g. WFS's TradeItem.eachDepth vs MP_ITEM's
        // Visible.assembledProductHeight) and everything renders blank.
        const retryFeedType = retryItem.feed_type || "MP_ITEM";
        return (
            <div className="flex-1 min-w-0 max-w-2xl">
                <div className="flex items-center gap-3 mb-8">
                    <button onClick={onBack} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition">
                        <ChevronLeft size={20} />
                    </button>
                    <h1 className="text-2xl font-bold text-gray-800">Retry Item</h1>
                </div>
                <WalmartItemForm
                    productType={productType}
                    identifierType={retryItem.payload?.Orderable?.productIdentifiers?.productIdType}
                    identifierValue={retryItem.payload?.Orderable?.productIdentifiers?.productId}
                    imageUrl={retryItem.image_url}
                    itemId={retryItem.item_id}
                    batchId={batchId}
                    feedType={retryFeedType}
                    version={WFS_MP_ITEM_VERSION}
                    onAddToBatch={onAddToBatch}
                    onBack={onBack}
                    titleEditable
                    initialItem={retryItem}
                />
            </div>
        );
    }

    return (
        <div className="flex-1 min-w-0 max-w-2xl">
            <div className="flex items-center gap-3 mb-8">
                <button onClick={onBack} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition">
                    <ChevronLeft size={20} />
                </button>
                <div>
                    <h1 className="text-2xl font-bold text-gray-800">{isNew ? "Create New Listing" : "Item Details"}</h1>
                    <p className="text-gray-500 text-sm">
                        {batchType.id === "walmart_wfs" ? "Walmart Fulfillment Services (WFS)" : "Seller Fulfilled (SF)"}
                    </p>
                </div>
            </div>

            {!isNew && batchType.id === "walmart_sf" && (
                <MPItemFlow initialProduct={initialProduct} batchId={batchId} onAddToBatch={onAddToBatch} onBack={onBack} buylistFields={buylistFields} />
            )}

            {!isNew && batchType.id === "walmart_wfs" && (
                <MPWfsItemFlow initialProduct={initialProduct} batchId={batchId} onAddToBatch={onAddToBatch} onBack={onBack} buylistFields={buylistFields} />
            )}

            {isNew && (
                <WalmartNewListingFlow
                    batchId={batchId}
                    onAddToBatch={onAddToBatch}
                    onBack={onBack}
                    feedType={batchType.id === "walmart_wfs" ? "MP_WFS_ITEM" : "MP_ITEM"}
                    version={WFS_MP_ITEM_VERSION}
                    prefillUpc={prefillUpc}
                    prefillName={prefillName}
                    prefillDescription={prefillDescription}
                    prefillImageUrl={prefillImageUrl}
                    buylistFields={buylistFields}
                />
            )}
        </div>
    );
};

export default WalmartItemDetails;